import { GamePlayScene } from '../scenes/GamePlayScene';
import { GamePlayUIManager, GameState } from './GamePlayUIManager';
import { TowerPlacementManager } from './TowerPlacementManager';
import { MonsterSpawnManager } from './MonsterSpawnManager';
import { EarthAbilityManager } from './EarthAbilityManager';
import { RewardManager } from './RewardManager';
import { AdBoostManager } from './AdBoostManager';
import { PetGamePlayManager } from './PetGamePlayManager';
import { Earth } from '../entities/earth/Earth';
import { ClassicEarth } from '../entities/earth/ClassicEarth';
import { IceEarth } from '../entities/earth/IceEarth';
import { VolcanicEarth } from '../entities/earth/VolcanicEarth';
import { CrystalEarth } from '../entities/earth/CrystalEarth';
import { QuantumEarth } from '../entities/earth/QuantumEarth';
import { NeutronEarth } from '../entities/earth/NeutronEarth';
import { Tower } from '../entities/towers/Tower';
import { Monster } from '../entities/monsters/Monster';
import { Boss } from '../entities/bosses/Boss';
import { Pet } from '../entities/pets/Pet';
import { UserProfile } from '../entities/base/UserProfile';
import { AudioManager, BackgroundMusic, SoundEffect } from '../systems/AudioManager';

export interface GamePlayState {
  earth: Earth | null;
  towers: Tower[];
  monsters: Monster[];
  bosses: Boss[];
  energyCredits: number;
  currentLevel: number;
  currentXP: number;
  xpToNextLevel: number;
  monsterKills: number;
  bossKills: number;
  sessionTime: number;
  isGameOver: boolean;
  currentBossFight: boolean;
  pauseGame: boolean;
}

export class GamePlayManager {
  private scene: GamePlayScene;
  private audioManager: AudioManager;
  private userProfile: UserProfile;
  // Core game state
  private gameState: GamePlayState;
  
  // Managers
  private uiManager: GamePlayUIManager;
  private towerManager: TowerPlacementManager;
  private spawnManager: MonsterSpawnManager;
  private earthAbilityManager: EarthAbilityManager;
  private rewardManager: RewardManager;
  private adBoostManager: AdBoostManager;
  private petManager: PetGamePlayManager;

  constructor(scene: GamePlayScene) {
    this.scene = scene;
    this.audioManager = AudioManager.getInstance(scene);
    this.userProfile = (window as any).userProfile;
    
    // Initialize game state
    this.gameState = {
      earth: null,
      towers: [],
      monsters: [],
      bosses: [],
      energyCredits: 1000,
      currentLevel: 1,
      currentXP: 0,
      xpToNextLevel: 100,
      monsterKills: 0,
      bossKills: 0,
      sessionTime: 0,
      isGameOver: false,
      currentBossFight: false,
      pauseGame: false
    };
    
    this.initializeManagers();
  }

  private initializeManagers(): void {
    // Initialize all managers with scene reference
    this.uiManager = new GamePlayUIManager(this.scene);
    this.towerManager = new TowerPlacementManager(this.scene);
    this.spawnManager = new MonsterSpawnManager(this.scene);
    this.earthAbilityManager = new EarthAbilityManager(this.scene);
    this.rewardManager = new RewardManager(this.scene);
    this.adBoostManager = new AdBoostManager(this.scene);
    this.petManager = new PetGamePlayManager(this.scene);
    
    // Initialize reward manager from profile
    this.rewardManager.initializeNewGame();
    this.gameState.currentLevel = 1;
    this.gameState.currentXP = 0;
    this.gameState.xpToNextLevel = this.rewardManager.getXPToNextLevel();
  }

  initializeGame(): void {
    // Initialize Earth
    this.initializeEarth();
    
    // Create UI
    this.createUI();
    
    // Start spawning with callback
    this.spawnManager.startSpawning(this.gameState.currentLevel);
    
    // Set up event listener for monster spawning
    this.scene.events.on('monsterSpawned', (spawnResult: any) => {
      this.gameState.monsters.push(...spawnResult.monsters);
      if (spawnResult.boss) {
        this.gameState.bosses.push(spawnResult.boss);
        this.gameState.monsters.push(spawnResult.boss);
        this.gameState.currentBossFight = true;
        this.audioManager.playMusic(BackgroundMusic.BOSS_FIGHT);
      }
    });
    
    // Play background music
    this.audioManager.playMusic(BackgroundMusic.GAMEPLAY);
  }

  private initializeEarth(): void {
    const selectedEarthType = this.userProfile.equippedEarth || 'classic';
    this.gameState.earth = this.createEarthByType(selectedEarthType);
    
    if (this.gameState.earth) {
      // Position earth at center
      this.gameState.earth.x = this.scene.cameras.main.centerX;
      this.gameState.earth.y = this.scene.cameras.main.centerY;
      this.scene.add.existing(this.gameState.earth);
    }
  }

  private createEarthByType(earthType: string): Earth | null {
    const centerX = this.scene.cameras.main.centerX;
    const centerY = this.scene.cameras.main.centerY;
    
    switch (earthType) {
      case 'classic':
        return new ClassicEarth(this.scene, centerX, centerY);
      case 'ice':
        return new IceEarth(this.scene, centerX, centerY);
      case 'volcanic':
        return new VolcanicEarth(this.scene, centerX, centerY);
      case 'crystal':
        return new CrystalEarth(this.scene, centerX, centerY);
      case 'quantum':
        return new QuantumEarth(this.scene, centerX, centerY);
      case 'neutron':
        return new NeutronEarth(this.scene, centerX, centerY);
      default:
        return new ClassicEarth(this.scene, centerX, centerY);
    }
  }

  private createUI(): void {
    // Create game UI
    const gameStateForUI: GameState = {
      earth: this.gameState.earth,
      energyCredits: this.gameState.energyCredits,
      currentLevel: this.gameState.currentLevel,
      currentXP: this.gameState.currentXP,
      xpToNextLevel: this.gameState.xpToNextLevel,
      monsterKills: this.gameState.monsterKills,
      towers: this.gameState.towers,
      bossKills: this.gameState.bossKills
    };
    
    this.uiManager.createGameUI(gameStateForUI);
    
    if (this.gameState.earth) {
      this.uiManager.createEarthDisplay(this.gameState.earth, this.gameState.towers);
    }
    
    // Create tower UI
    const equippedTowers = this.userProfile.equippedTowers || ['laser', 'rapid', 'heavy'];
    this.uiManager.createTowerUI(equippedTowers);
    
    // Create pet UI
    const equippedPets = this.userProfile.equippedPets || ['laser'];
    this.petManager.createPetPanel(equippedPets);
    
    // Create ad UI
    this.adBoostManager.createAdUI();
  }

  update(time: number, delta: number): void {
    if (this.gameState.pauseGame) return;
    // Update session time
    this.gameState.sessionTime += delta;
    this.rewardManager.updateSessionTime(delta);
    
    // Update managers
    this.updateManagers(time, delta);
    
    // Update game logic
    this.updateGameLogic(time, delta);
    
    // Update UI
    this.updateUI();
  }

  private updateManagers(time: number, delta: number): void {
    // Update ad boosts
    this.adBoostManager.updateActiveBoosts(time);
    
    // Update pet behavior
    this.petManager.updatePetBehavior(time, delta);
    
    // Monster spawning is handled by MonsterSpawnManager timer, not here
    // Remove the manual spawning to avoid conflicts
  }

  private updateGameLogic(time: number, delta: number): void {
    // Update game objects
    if (this.gameState.earth) {
      this.gameState.earth.update(time, delta);
    }
    const monsters: Monster[] = [];
    const towers: Tower[] = [];
    this.scene.children.list.forEach(child => {
      if (child instanceof Monster || child instanceof Boss) {
        monsters.push(child as Monster);
      }
      if (child instanceof Tower) {
        towers.push(child as Tower);
      }
    });
    // Share monsters list and earth with scene for towers and monsters to access
    this.gameState.monsters = monsters;
    (this.scene as any).monsters = monsters;
    (this.scene as any).earth = this.gameState.earth;
    // Debug logging
    // if (time % 2000 < delta) { // Every 2 seconds
    //   console.log(`GamePlayManager: ${this.gameState.monsters.length} monsters, ${this.gameState.towers.length} towers`);
    //   if (this.gameState.monsters.length > 0) {
    //     const firstMonster = this.gameState.monsters[0];
    //     if (firstMonster) {
    //       console.log(`First monster pos: x=${firstMonster.x}, y=${firstMonster.y}`);
    //     }
    //   }
    //   if (this.gameState.towers.length > 0) {
    //     const firstTower = this.gameState.towers[0];
    //     if (firstTower) {
    //       console.log(`First tower pos: x=${firstTower.x}, y=${firstTower.y}`);
    //     }
    //   }
    // }
    
    this.gameState.towers.forEach(tower => tower.update(time, delta));
    this.gameState.monsters.forEach(monster => monster.update(time, delta));
    
    // Check for dead monsters
    this.checkDeadMonsters();
    
    // Check for level up
    this.checkLevelUp();
    
    // Check game over
    this.checkGameOver();
    
    // Make pet follow or orbit earth
    if (this.gameState.earth && this.petManager.hasPet()) {
      this.petManager.makePetOrbitEarth(this.gameState.earth, time);
    }
    this.gameState.currentLevel = this.rewardManager.getGameLevel();
    this.gameState.currentXP = this.rewardManager.getGameXP();
    this.gameState.xpToNextLevel = this.rewardManager.getXPToNextLevel();
  }

  private checkDeadMonsters(): void {
    for (let i = this.gameState.monsters.length - 1; i >= 0; i--) {
      if (!this.gameState.monsters[i]) {
        this.gameState.monsters.splice(i, 1);
      }
      const monster = this.gameState.monsters[i] as Monster;
      if (monster.getHealth() <= 0) {
        // Handle rewards
        const isBoss = monster instanceof Boss;
        let xpGained = 0;
        if (isBoss) {
          this.gameState.bossKills++;
          xpGained = this.rewardManager.onBossKilled(monster.constructor.name);
          this.uiManager.showXPGain(monster.x, monster.y, xpGained);
          this.gameState.currentBossFight = false;
          this.audioManager.playMusic(BackgroundMusic.GAMEPLAY);
          // Remove from bosses array too
          const bossIndex = this.gameState.bosses.indexOf(monster as Boss);
          if (bossIndex >= 0) {
            this.gameState.bosses.splice(bossIndex, 1);
          }
        } else {
          this.gameState.monsterKills++;
          xpGained = this.rewardManager.onMonsterKilled(monster.constructor.name);
          this.uiManager.showXPGain(monster.x, monster.y, xpGained);
        }
        // Play death sound
        if (isBoss) {
          this.audioManager.playSFX(SoundEffect.BOSS_DEATH);
        } else {
          this.audioManager.playSFX(SoundEffect.MONSTER_DEATH);
        }
        // Create death effect
        this.createDeathEffect(monster.x, monster.y);
        // Remove monster
        monster.destroy();
        this.gameState.monsters.splice(i, 1);
      }
    }
    // Sau khi cộng XP, cập nhật lại level/xp UI nếu có thay đổi
    this.gameState.currentLevel = this.rewardManager.getGameLevel();
    this.gameState.currentXP = this.rewardManager.getGameXP();
    this.gameState.xpToNextLevel = this.rewardManager.getXPToNextLevel();
  }

  private checkLevelUp(): void {
    const progressStats = this.rewardManager.getProgressStats();
    if (progressStats.gameXP >= progressStats.xpToNextLevel) {
      const levelUpResult = this.rewardManager.levelUp();
      if (levelUpResult) {
        this.gameState.currentLevel = progressStats.gameLevel;
        this.gameState.currentXP = progressStats.gameXP;
        this.gameState.xpToNextLevel = progressStats.xpToNextLevel;
        
        // Update spawn difficulty
        this.spawnManager.updateDifficultySettings(this.gameState.currentLevel);
      }
    }
  }

  private checkGameOver(): void {
    if (this.gameState.earth && this.gameState.earth.getHealth() <= 0 && !this.gameState.isGameOver) {
      this.gameState.isGameOver = true;
      this.showGameOverWithReviveOption();
    }
  }

  private updateUI(): void {
    const progressStats = this.rewardManager.getProgressStats();
    const gameStateForUI: GameState = {
      earth: this.gameState.earth,
      energyCredits: this.gameState.energyCredits,
      currentLevel: progressStats.gameLevel,
      currentXP: progressStats.gameXP,
      xpToNextLevel: progressStats.xpToNextLevel,
      monsterKills: this.gameState.monsterKills,
      bossKills: this.gameState.bossKills,
      towers: this.gameState.towers
    };
    this.uiManager.updateGameUI(gameStateForUI);
  }

  // Public methods for UI interactions
  startTowerPlacement(towerType: string): void {
    const success = this.towerManager.startTowerPlacement(towerType, this.gameState.energyCredits);
    if (!success) {
      this.uiManager.showNotification('Not enough credits!', 2000, '#ff6b6b');
    }
  }

  placeTowerAt(x: number, y: number): void {
    if (!this.gameState.earth) return;
    
    const result = this.towerManager.placeTower(
      this.gameState.earth.x, this.gameState.earth.y, 
      this.gameState.earth, 
      this.gameState.energyCredits, 
      this.gameState.towers
    );
    
    if (result.success && result.tower) {
      this.gameState.energyCredits -= result.cost;
      this.gameState.towers.push(result.tower);
      
      // Only validate positioning, don't force rearrange
      this.towerManager.updateTowerPositions(this.gameState.earth, this.gameState.towers);
      this.uiManager.showNotification(`Tower placed! (-${result.cost} credits)`, 1500, '#4CAF50');
    } else {
      this.uiManager.showNotification(result.message || 'Cannot place tower', 2000, '#ff6b6b');
    }
  }

  tryActivateEarthAbility(): void {
    const success = this.earthAbilityManager.tryActivateEarthAbility(this.gameState.earth);
    if (!success) {
      this.uiManager.showNotification('Ability on cooldown!', 1500, '#ffaa00');
    }
  }

  upgradeEarth(): void {
    const result = this.earthAbilityManager.upgradeEarth(this.gameState.earth, this.gameState.energyCredits);
    if (result.success) {
      this.gameState.energyCredits = result.newCredits;
      this.uiManager.showNotification(`Earth upgraded! Level ${result.newLevel}`, 2000, '#4CAF50');
    } else {
      this.uiManager.showNotification(`Need ${result.cost} credits to upgrade`, 2000, '#ff6b6b');
    }
  }

  upgradeTowerFromManager(tower: Tower): void {
    const result = this.towerManager.upgradeTower(tower, this.gameState.energyCredits);
    if (result.success) {
      this.gameState.energyCredits = result.newCredits;
      this.uiManager.showNotification(`Tower upgraded! (-${result.cost} credits)`, 1500, '#4CAF50');
    } else {
      this.uiManager.showNotification(`Need ${result.cost} credits to upgrade`, 2000, '#ff6b6b');
    }
  }

  sellTowerFromManager(tower: Tower): void {
    if (!this.gameState.earth) return;
    
    const sellValue = Math.floor(tower.getLevel() * 50);
    this.gameState.energyCredits += sellValue;
    this.towerManager.sellTower(tower, this.gameState.earth, this.gameState.towers);
    this.uiManager.showNotification(`Tower sold! (+${sellValue} credits)`, 1500, '#FFD700');
  }

  upgradePetFromManager(pet: Pet): void {
    const result = this.petManager.upgradePet(pet, this.gameState.energyCredits);
    if (result.success) {
      this.gameState.energyCredits = result.newCredits;
      this.uiManager.showNotification(`Pet upgraded! Level ${result.newLevel}`, 1500, '#4CAF50');
    } else {
      this.uiManager.showNotification(`Need ${result.cost} credits to upgrade`, 2000, '#ff6b6b');
    }
  }

  spawnPet(petType: string): void {
    const pet = this.petManager.spawnActivePet(petType);
    if (pet) {
      this.uiManager.showNotification(`${petType} pet summoned!`, 1500, '#00ff88');
    }
  }

  watchAdForBoost(boostType: string): void {
    const success = this.adBoostManager.watchAdForBoost(boostType);
    if (!success) {
      this.uiManager.showNotification('Cannot watch ad right now', 2000, '#ff6b6b');
    }
  }

  addEnergyCredits(amount: number): void {
    // Apply credit boost multiplier
    const multiplier = this.adBoostManager.getBoostMultiplier('double_credits');
    const finalAmount = Math.floor(amount * multiplier);
    
    this.gameState.energyCredits += finalAmount;
    
    if (multiplier > 1) {
      this.uiManager.showNotification(`+${finalAmount} credits (${multiplier}x boost!)`, 1000, '#FFD700');
    }
  }

  private createDeathEffect(x: number, y: number): void {
    // Create simple death effect
    for (let i = 0; i < 5; i++) {
      const particle = this.scene.add.circle(x, y, 2, 0xff4444);
      this.scene.tweens.add({
        targets: particle,
        x: x + (Math.random() - 0.5) * 50,
        y: y + (Math.random() - 0.5) * 50,
        alpha: 0,
        duration: 500,
        ease: 'Power2',
        onComplete: () => particle.destroy()
      });
    }
  }

  private showGameOverWithReviveOption(): void {
    this.spawnManager.stopSpawning();
    
    // Check if can watch ad for revive
    if (this.adBoostManager.getRemainingAds() > 0) {
      this.showReviveDialog();
    } else {
      this.goToGameOver();
    }
  }

  private showReviveDialog(): void {
    const reviveDialog = this.scene.add.container(
      this.scene.cameras.main.centerX,
      this.scene.cameras.main.centerY
    );
    
    // Background
    const bg = this.scene.add.rectangle(0, 0, 300, 200, 0x000000, 0.8);
    reviveDialog.add(bg);
    
    // Title
    const title = this.scene.add.text(0, -60, 'Game Over!', {
      fontSize: '24px',
      color: '#ff6b6b',
      fontFamily: 'Arial',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    reviveDialog.add(title);
    
    // Revive button
    const reviveBtn = this.scene.add.text(0, -10, 'Watch Ad to Revive', {
      fontSize: '16px',
      color: '#ffffff',
      backgroundColor: '#4CAF50',
      padding: { x: 15, y: 8 },
      fontFamily: 'Arial'
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    
    reviveBtn.on('pointerdown', () => {
      const success = this.adBoostManager.watchAdForRevive();
      if (success) {
        reviveDialog.destroy();
        this.reviveFromAd();
        this.gameState.pauseGame = false;
      }
    });
    reviveDialog.add(reviveBtn);
    
    // Skip button
    const skipBtn = this.scene.add.text(0, 40, 'Continue to Results', {
      fontSize: '14px',
      color: '#ffffff',
      backgroundColor: '#666666',
      padding: { x: 12, y: 6 },
      fontFamily: 'Arial'
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    
    skipBtn.on('pointerdown', () => {
      reviveDialog.destroy();
      this.goToGameOver();
    });
    reviveDialog.add(skipBtn);
    this.gameState.pauseGame = true;
  }

  reviveFromAd(): void {
    if (this.gameState.earth) {
      this.gameState.earth.heal(this.gameState.earth.getMaxHealth());
      this.gameState.isGameOver = false;
      this.spawnManager.startSpawning(this.gameState.currentLevel);
      this.uiManager.showNotification('Revived! Fight on!', 2000, '#4CAF50');
    }
  }

  private goToGameOver(): void {
    // Save final stats to profile
    const finalStats = this.rewardManager.getProgressStats();
    this.userProfile.totalSessionTime += finalStats.sessionTime;
    this.userProfile.totalMonsterKills += finalStats.totalMonsterKills;
    this.userProfile.totalBossKills += finalStats.totalBossKills;
    // Cộng XP vào profile
    RewardManager.addXPToProfile(this.userProfile, finalStats.gameXP);
    // Cập nhật highestLevel nếu đạt cao hơn
    this.userProfile.highestLevel = Math.max(this.userProfile.highestLevel, finalStats.gameLevel);
    // this.userProfile.updateProgress(
    //   finalStats.currentLevel,
    //   finalStats.currentXP,
    //   finalStats.totalMonsterKills,
    //   finalStats.totalBossKills
    // );
    // Transition to game over scene
    this.scene.scene.start('GameOver', {
      level: finalStats.gameLevel,
      monsterKills: finalStats.totalMonsterKills,
      bossKills: finalStats.totalBossKills,
      timePlayed: finalStats.sessionTime
    });
  }

  // Getters for external access
  getGameState(): GamePlayState {
    return { ...this.gameState };
  }

  getTowerManager(): TowerPlacementManager {
    return this.towerManager;
  }

  getUIManager(): GamePlayUIManager {
    return this.uiManager;
  }

  getSpawnManager(): MonsterSpawnManager {
    return this.spawnManager;
  }
} 